import { useRef, useState, type ChangeEvent } from "react";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { PageLoader } from "@/components/ui/Spinner";
import type { MentionSuggestion } from "@/components/ui/RichTextEditor";
import DOMPurify from "dompurify";
import { highlightMentions, highlightMentionsInHtml } from "@/utils/highlightMentions";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import { useAuthStore } from "@/store/auth";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useConversationDeepLink } from "@/hooks/useConversationDeepLink";
import {
  useClientInvoice,
  useClientInvoiceEvents,
  useAddClientInvoiceComment,
  useDeleteClientInvoicePaymentEvent,
  useUpdateClientInvoicePayment,
  useUpdateClientInvoice,
  useDeleteClientInvoice,
  useApprovePrzDraft,
  useRevokeApprovalPrzDraft,
  useSetExpectedPaymentDate,
  useClientInvoiceLineItems,
  useCreateLineItem,
  useUpdateLineItem,
  useDeleteLineItem,
  useConvertProforma,
  useAnalyzeClientInvoice,
} from "@/features/finances/hooks/useFinances";
import { JointVentureSection } from "@/features/finances/components/JointVentureSection";
import { ClientInvoiceAiPanel } from "@/features/finances/components/ClientInvoiceAiPanel";
import type { ClientInvoice } from "@/features/finances/types";
import {
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  CLIENT_INVOICE_STATUS_LABELS,
  CLIENT_INVOICE_STATUS_COLORS,
  CLIENT_INVOICE_EVENT_LABELS,
  INVOICE_TYPE_LABELS,
  PAYMENT_TERM_DAY_OPTIONS,
} from "@/features/finances/types";

function isHtmlContent(s: string) {
  return s.trimStart().startsWith("<");
}

function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "img", "h1", "h2", "h3"],
    ALLOWED_ATTR: ["src", "alt", "width", "height", "start"],
  });
}

function formatDateTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("pl-PL", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function availableActions(inv: ClientInvoice, role?: string) {
  const draft = inv.status === "draft";
  const issued = inv.status === "issued";
  const paid = inv.payment_status === "paid";
  const approved = inv.director_approved === true;
  return {
    issue: draft && approved,
    issueBlocked: draft && !approved,
    approve: draft && role === "director" && !approved,
    revokeApproval: draft && role === "director" && approved,
    revertToDraft: issued && !paid && !inv.sent_to_investor,
    uploadFile: issued,
    markSentToInvestor: issued && !inv.sent_to_investor,
    revertSentToInvestor: issued && inv.sent_to_investor && inv.payment_status === "unpaid",
    payment: issued && !paid,
    delete: draft || (issued && !paid && (role === "director" || role === "accountant")),
  };
}

const chip = "text-xs px-2.5 py-1 rounded border font-medium transition-colors";

export function ClientInvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canEdit = user?.role === "director" || user?.role === "accountant";
  // Footer edit mirrors backend IsTechnicalDirectorOrAccountant: CEO, accountant, or technical director.
  const canEditFooter =
    user?.is_ceo === true
    || user?.role === "accountant"
    || (user?.role === "director" && user?.is_technical_director !== false);

  const { data: invoice, isLoading } = useClientInvoice(id);
  usePageTitle(invoice && `Faktura PRZ ${invoice.invoice_number || "(szkic)"}`);
  const { data: events = [], isLoading: eventsLoading } = useClientInvoiceEvents(id);
  // Powiadomienie o oznaczeniu w komentarzu prowadzi do historii zdarzeń (tam żyje wątek).
  const { ref: eventsRef } = useConversationDeepLink<HTMLDivElement>(
    !isLoading && !eventsLoading && !!invoice,
  );
  const addComment = useAddClientInvoiceComment(id);
  const deletePaymentEvent = useDeleteClientInvoicePaymentEvent(id, invoice?.project);
  const updatePayment = useUpdateClientInvoicePayment(invoice?.project);
  const updateMutation = useUpdateClientInvoice(invoice?.project);
  const deleteMutation = useDeleteClientInvoice(invoice?.project);
  const approveMutation = useApprovePrzDraft();
  const revokeMutation = useRevokeApprovalPrzDraft();
  const { data: allUsers = [] } = useInternalUsers();
  const mentionSuggestions: MentionSuggestion[] = allUsers.map((u) => ({
    id: u.id,
    name: u.full_name,
    role: u.role,
    is_ceo: u.is_ceo,
  }));
  // Analiza AI pliku faktury idzie za dostępem do samej faktury: kto ją tu otworzył,
  // ten widzi werdykt i może go odświeżyć (backend rozstrzyga po dostępie do projektu).
  const analyzeInvoice = useAnalyzeClientInvoice(id);
  const { data: lineItems = [] } = useClientInvoiceLineItems(id);
  const createLineItem = useCreateLineItem(id!);
  const updateLineItem = useUpdateLineItem(id!);
  const deleteLineItem = useDeleteLineItem(id!);
  const convertProforma = useConvertProforma(id!);

  const [comment, setComment] = useState("");
  const [commentMentionIds, setCommentMentionIds] = useState<string[]>([]);
  const [commentFiles, setCommentFiles] = useState<File[]>([]);
  const commentFileRef = useRef<HTMLInputElement>(null);

  // Payment form
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [payForm, setPayForm] = useState({ new_payment_amount: "", payment_date: "", payment_comment: "" });

  // Issue modal
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueFile, setIssueFile] = useState<File | null>(null);
  const [issueNumber, setIssueNumber] = useState("");
  const [issueDate, setIssueDate] = useState("");

  // Expected payment date
  const [showExpectedDateForm, setShowExpectedDateForm] = useState(false);
  const [expectedDateInput, setExpectedDateInput] = useState("");
  const setExpectedDate = useSetExpectedPaymentDate(id);

  // Invoice issue date (editable only while DRAFT — same permissions as issuing FV)
  const [showInvoiceDateForm, setShowInvoiceDateForm] = useState(false);
  const [invoiceDateInput, setInvoiceDateInput] = useState("");
  const [showTermsForm, setShowTermsForm] = useState(false);

  // Line items — new row draft
  const [newRow, setNewRow] = useState<{ name: string; unit_value: string; quantity: string } | null>(null);
  // Line items — editing existing row
  const [editingItem, setEditingItem] = useState<{ id: string; name: string; unit_value: string; quantity: string } | null>(null);

  // Net amount inline edit
  const [showNetEdit, setShowNetEdit] = useState(false);
  const [netInput, setNetInput] = useState("");
  const [vatRate, setVatRate] = useState("23");

  // Convert proforma panel
  const [showConvertPanel, setShowConvertPanel] = useState(false);
  const [convertNumber, setConvertNumber] = useState("");
  const [convertFile, setConvertFile] = useState<File | null>(null);
  const [convertErr, setConvertErr] = useState<string | null>(null);

  // File upload
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Footer text editor
  const [showFooterModal, setShowFooterModal] = useState(false);
  const [footerDraft, setFooterDraft] = useState("");

  const handleAddComment = async () => {
    const msg = comment.trim();
    if (!msg) return;
    await addComment.mutateAsync({ message: msg, mentionIds: commentMentionIds, files: commentFiles.length > 0 ? commentFiles : undefined });
    setComment("");
    setCommentMentionIds([]);
    setCommentFiles([]);
    if (commentFileRef.current) commentFileRef.current.value = "";
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !invoice) return;
    updateMutation.mutate({ id: invoice.id, data: { invoice_file: file } });
    e.target.value = "";
  };

  const handleIssue = () => {
    if (!invoice) return;
    const num = issueNumber.trim() || invoice.invoice_number;
    const data: Record<string, unknown> = { status: "issued", invoice_number: num };
    if (issueFile) data.invoice_file = issueFile;
    if (issueDate && issueDate !== invoice.invoice_date) data.invoice_date = issueDate;
    updateMutation.mutate(
      { id: invoice.id, data },
      { onSuccess: () => { setShowIssueModal(false); setIssueFile(null); setIssueNumber(""); setIssueDate(""); } }
    );
  };

  const handleDelete = () => {
    if (!invoice) return;
    const msg = invoice.status === "issued"
      ? `Usunąć wystawioną fakturę ${invoice.invoice_number}? Tej operacji nie można cofnąć.`
      : "Usunąć ten szkic faktury PRZ? Tej operacji nie można cofnąć.";
    if (!confirm(msg)) return;
    deleteMutation.mutate(invoice.id, {
      onSuccess: () => navigate("/finances/invoices-prz"),
    });
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (!invoice) {
    return <p className="text-sm text-red-500 p-6">Nie znaleziono faktury.</p>;
  }

  const actions = availableActions(invoice, user?.role);
  const hasAnyAction = Object.values(actions).some(Boolean);

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6 px-3 py-4 sm:px-6 sm:py-6">
      {/* Breadcrumb */}
      <div className="text-xs text-gray-500 dark:text-gray-400">
        <Link to="/finances/invoices-prz" className="hover:underline text-[var(--color-primary)]">
          Faktury PRZ
        </Link>
        {" / "}
        <span className="text-gray-700 dark:text-gray-300 font-medium">
          {invoice.invoice_number || "Szkic (bez numeru)"}
        </span>
      </div>

      {/* Header card */}
      <div className="bg-white dark:bg-gray-800 p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {invoice.invoice_number || <span className="italic text-gray-400">Szkic (bez numeru)</span>}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{invoice.project_name}</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge color={CLIENT_INVOICE_STATUS_COLORS[invoice.status]}>
                {invoice.status === "draft" && invoice.director_approved !== true
                  ? "Szkic — niezatwierdzony"
                  : CLIENT_INVOICE_STATUS_LABELS[invoice.status]}
              </Badge>
              <Badge color={PAYMENT_STATUS_COLORS[invoice.payment_status]}>
                {PAYMENT_STATUS_LABELS[invoice.payment_status]}
              </Badge>
              {invoice.is_advance_invoice && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">Zaliczkowa</span>
              )}
              {invoice.invoice_type === "proforma" && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                  {INVOICE_TYPE_LABELS.proforma}
                </span>
              )}
              {invoice.invoice_type === "proforma_advance" && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                  {INVOICE_TYPE_LABELS.proforma_advance}
                </span>
              )}
              {invoice.director_approved === true && invoice.status === "draft" && (
                <span className="text-xs text-green-600 dark:text-green-400 border border-green-300 px-2 py-0.5 rounded font-medium">Zatwierdzony szkic</span>
              )}

              {invoice.status === "issued" && (
                invoice.sent_to_investor ? (
                  <span className="text-xs font-medium text-teal-700 bg-teal-50 border border-teal-300 px-2 py-0.5 rounded">
                    ✓ Wysłano do inwestora
                    {invoice.sent_to_investor_at && (
                      <span className="font-normal text-teal-500 ml-1">
                        {new Date(invoice.sent_to_investor_at).toLocaleDateString("pl-PL")}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-300 px-2 py-0.5 rounded">
                    ⚠ Nie wysłano do inwestora
                  </span>
                )
              )}
            </div>
          </div>
          {invoice.invoice_managed_file_detail?.url && (
            <a
              href={invoice.invoice_managed_file_detail.url}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-[var(--color-primary)] hover:underline font-medium flex-shrink-0"
            >
              📎 Pobierz plik
            </a>
          )}
        </div>

        {/* Action chips — same logic as the list page */}
        {canEdit && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">Dostępne akcje</p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => updateMutation.mutate({ id: invoice.id, data: { is_advance_invoice: !invoice.is_advance_invoice } })}
                disabled={updateMutation.isPending}
                className={`${chip} ${invoice.is_advance_invoice ? "border-violet-300 text-violet-700 bg-violet-50 hover:bg-violet-100" : "border-gray-300 text-gray-600 dark:text-gray-400 dark:border-gray-600 hover:bg-gray-50"}`}
              >
                {invoice.is_advance_invoice ? "✓ Faktura zaliczkowa" : "Faktura zaliczkowa"}
              </button>
              {actions.approve && (
                <button
                  onClick={() => approveMutation.mutate(invoice.id)}
                  disabled={approveMutation.isPending || lineItems.length === 0}
                  title={lineItems.length === 0 ? "Dodaj pozycje faktury przed zatwierdzeniem" : undefined}
                  className={`${chip} ${lineItems.length === 0 ? "border-gray-200 text-gray-400 cursor-not-allowed" : "border-green-300 text-green-700 hover:bg-green-50"}`}
                >
                  Zatwierdź szkic
                </button>
              )}
              {actions.revokeApproval && (
                <button
                  onClick={() => revokeMutation.mutate(invoice.id)}
                  disabled={revokeMutation.isPending}
                  className={`${chip} border-orange-300 text-orange-600 hover:bg-orange-50`}
                >
                  Cofnij zatwierdzenie
                </button>
              )}
              {actions.issue && (
                <button
                  onClick={() => { setShowIssueModal(true); setIssueNumber(invoice.invoice_number || ""); setIssueDate(invoice.invoice_date || ""); }}
                  className={`${chip} border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)]`}
                >
                  Wystaw fakturę
                </button>
              )}
              {actions.issueBlocked && (
                <button
                  disabled
                  title="Szkic niezatwierdzony przez dyrektora"
                  className={`${chip} border-gray-200 text-gray-400 cursor-not-allowed`}
                >
                  Wystaw fakturę
                </button>
              )}
              {actions.revertToDraft && (
                <button
                  onClick={() => updateMutation.mutate({ id: invoice.id, data: { status: "draft" } })}
                  disabled={updateMutation.isPending}
                  className={`${chip} border-gray-300 text-gray-600 dark:text-gray-400 dark:border-gray-600 hover:bg-gray-50`}
                >
                  Cofnij do szkicu
                </button>
              )}
              {actions.uploadFile && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={`${chip} border-purple-300 text-purple-600 hover:bg-purple-50`}
                >
                  {invoice.invoice_managed_file_detail?.url ? "Zmień plik" : "Wgraj plik"}
                </button>
              )}

              {actions.markSentToInvestor && (
                <button
                  onClick={() => updateMutation.mutate({ id: invoice.id, data: { sent_to_investor: true } })}
                  disabled={updateMutation.isPending}
                  className={`${chip} border-teal-300 text-teal-700 hover:bg-teal-50`}
                >
                  Oznacz jako wysłane do inwestora
                </button>
              )}
              {actions.revertSentToInvestor && (
                <button
                  onClick={() => updateMutation.mutate({ id: invoice.id, data: { sent_to_investor: false } })}
                  disabled={updateMutation.isPending}
                  className={`${chip} border-gray-300 text-gray-500 dark:text-gray-400 dark:border-gray-600 hover:bg-gray-50`}
                >
                  Cofnij wysłanie do inwestora
                </button>
              )}
              {actions.payment && (
                <button
                  onClick={() => { setPayForm({ new_payment_amount: "", payment_date: "", payment_comment: "" }); setShowPaymentForm(true); }}
                  className={`${chip} border-green-300 text-green-700 hover:bg-green-50`}
                >
                  Zarejestruj płatność
                </button>
              )}
              {actions.delete && (
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className={`${chip} border-red-300 text-red-500 hover:bg-red-50`}
                >
                  {invoice.status === "issued" ? "Usuń fakturę" : "Usuń szkic"}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          <ClientInvoiceAiPanel
            analysis={invoice.ai_analysis}
            hasFile={!!invoice.invoice_managed_file_detail?.url}
            onRerun={() => analyzeInvoice.mutate()}
            rerunning={analyzeInvoice.isPending}
          />
        </div>

        {/* Convert proforma panel — shown when proforma is fully paid */}
        {invoice.is_proforma && invoice.payment_status === "paid" && invoice.invoice_type !== "regular" && canEdit && (
          <div className="mt-4 pt-4 border-t border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-b-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                🧾 Proforma opłacona — wystaw fakturę VAT
              </h3>
              <button
                onClick={() => setShowConvertPanel((v) => !v)}
                className="text-xs text-amber-700 dark:text-amber-300 hover:underline"
              >
                {showConvertPanel ? "Zwiń" : "Rozwiń"}
              </button>
            </div>
            {showConvertPanel && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                    Numer faktury VAT *
                  </label>
                  <input
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
                    value={convertNumber}
                    onChange={(e) => setConvertNumber(e.target.value)}
                    placeholder="np. FV/2025/01/001"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                    Plik faktury VAT *
                  </label>
                  <FileDropZone
                    accept="application/pdf,image/*"
                    files={convertFile ? [convertFile] : []}
                    onFilesChange={(fs) => setConvertFile(fs[0] ?? null)}
                  />
                </div>
                {convertErr && <p className="text-xs text-red-600">{convertErr}</p>}
                <div className="flex gap-2">
                  <Button
                    loading={convertProforma.isPending}
                    disabled={!convertNumber.trim() || !convertFile}
                    onClick={() => {
                      if (!convertNumber.trim()) { setConvertErr("Podaj numer faktury VAT."); return; }
                      if (!convertFile) { setConvertErr("Wybierz plik faktury VAT."); return; }
                      setConvertErr(null);
                      const fd = new FormData();
                      fd.append("invoice_number", convertNumber.trim());
                      fd.append("invoice_file", convertFile);
                      convertProforma.mutate(fd, {
                        onSuccess: () => {
                          setShowConvertPanel(false);
                          setConvertNumber("");
                          setConvertFile(null);
                        },
                        onError: () => setConvertErr("Błąd przekształcenia proformy."),
                      });
                    }}
                  >
                    Przekształć w FV VAT
                  </Button>
                  <button
                    type="button"
                    onClick={() => { setShowConvertPanel(false); setConvertErr(null); }}
                    className="text-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                  >
                    Anuluj
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payment form inline */}
        {showPaymentForm && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Zarejestruj wpływ</h3>
            {invoice.paid_amount !== "0.00" && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Dotychczas zapłacono: {formatAmount(invoice.paid_amount)} PLN z {formatAmount(invoice.amount_gross)} PLN brutto
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Kwota wpływu brutto (PLN) *</label>
                <input
                  type="number" step="0.01"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={payForm.new_payment_amount}
                  onChange={(e) => setPayForm({ ...payForm, new_payment_amount: e.target.value })}
                  placeholder="0.00" autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Wpłata netto (obliczona automatycznie)</label>
                <input
                  type="text" readOnly disabled
                  className="w-full rounded px-2 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                  value={(() => {
                    const gross = parseFloat(payForm.new_payment_amount);
                    const invGross = parseFloat(invoice.amount_gross);
                    const invNet = parseFloat(invoice.amount_net);
                    if (!isNaN(gross) && gross > 0 && invGross > 0)
                      return formatAmount(gross * invNet / invGross) + " PLN";
                    return "—";
                  })()}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Data wpływu *</label>
                <DatePicker
                  value={payForm.payment_date}
                  onChange={(v) => setPayForm({ ...payForm, payment_date: v })}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Komentarz</label>
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={payForm.payment_comment}
                  onChange={(e) => setPayForm({ ...payForm, payment_comment: e.target.value })}
                  placeholder="Opcjonalny komentarz"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm" loading={updatePayment.isPending}
                disabled={!payForm.new_payment_amount || !payForm.payment_date}
                onClick={async () => { await updatePayment.mutateAsync({ id: id!, data: payForm }); setShowPaymentForm(false); }}
              >
                Zapisz
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setShowPaymentForm(false)}>Anuluj</Button>
            </div>
          </div>
        )}

        {/* Line items table */}
        <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Pozycje faktury</h3>
              {invoice.status === "draft" && lineItems.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">Wymagane przed zatwierdzeniem — dodaj co najmniej jedną pozycję.</p>
              )}
            </div>
            {canEdit && invoice.status === "draft" && !newRow && (
              <button
                onClick={() => setNewRow({ name: "", unit_value: "", quantity: "1" })}
                className="text-xs px-2.5 py-1 rounded border border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)] font-medium"
              >
                + Dodaj pozycję
              </button>
            )}
          </div>
          {/* Mobile card list */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800">
            {lineItems.map((item, idx) => (
              <div key={item.id} className="py-2.5">
                {editingItem?.id === item.id ? (
                  <div className="space-y-2">
                    <input
                      autoFocus
                      className="w-full border border-[var(--color-primary)] rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
                      value={editingItem.name}
                      onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-gray-400 uppercase block mb-0.5">Wartość jedn.</label>
                        <input
                          type="number" step="0.01"
                          className="w-full border border-[var(--color-primary)] rounded px-2 py-1 text-sm text-right bg-white dark:bg-gray-700"
                          value={editingItem.unit_value}
                          onChange={(e) => setEditingItem({ ...editingItem, unit_value: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-400 uppercase block mb-0.5">Ilość</label>
                        <input
                          type="number" step="0.001" min="0.001"
                          className="w-full border border-[var(--color-primary)] rounded px-2 py-1 text-sm text-right bg-white dark:bg-gray-700"
                          value={editingItem.quantity}
                          onChange={(e) => setEditingItem({ ...editingItem, quantity: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={async () => { await updateLineItem.mutateAsync({ itemId: item.id, data: { name: editingItem.name, unit_value: editingItem.unit_value, quantity: editingItem.quantity } }); setEditingItem(null); }}
                        disabled={updateLineItem.isPending || !editingItem.name.trim()}
                        className="text-xs px-3 py-1 bg-[var(--color-primary)] text-white rounded disabled:opacity-50"
                      >Zapisz</button>
                      <button onClick={() => setEditingItem(null)} className="text-xs px-3 py-1 border border-gray-300 text-gray-500 rounded">Anuluj</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug">{idx + 1}. {item.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {formatAmount(item.unit_value)} × {item.quantity} =
                        <span className="font-semibold text-gray-900 dark:text-gray-100 ml-1">{formatAmount(item.total)} PLN</span>
                      </p>
                    </div>
                    {canEdit && invoice.status === "draft" && (
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => setEditingItem({ id: item.id, name: item.name, unit_value: item.unit_value, quantity: item.quantity })} className="text-[10px] px-1.5 py-0.5 border border-gray-300 text-gray-500 rounded hover:bg-gray-50">Ed.</button>
                        <button onClick={() => deleteLineItem.mutate(item.id)} disabled={deleteLineItem.isPending} className="text-[10px] px-1.5 py-0.5 border border-red-200 text-red-500 rounded hover:bg-red-50">Usuń</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {newRow && (
              <div className="py-2.5 space-y-2">
                <input
                  autoFocus
                  placeholder="Nazwa pozycji"
                  className="w-full border border-[var(--color-primary)] rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
                  value={newRow.name}
                  onChange={(e) => setNewRow({ ...newRow, name: e.target.value })}
                />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-gray-400 uppercase block mb-0.5">Wartość jedn.</label>
                    <input type="number" step="0.01" placeholder="0.00" className="w-full border border-[var(--color-primary)] rounded px-2 py-1 text-sm text-right bg-white dark:bg-gray-700" value={newRow.unit_value} onChange={(e) => setNewRow({ ...newRow, unit_value: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400 uppercase block mb-0.5">Ilość</label>
                    <input type="number" step="0.001" min="0.001" className="w-full border border-[var(--color-primary)] rounded px-2 py-1 text-sm text-right bg-white dark:bg-gray-700" value={newRow.quantity} onChange={(e) => setNewRow({ ...newRow, quantity: e.target.value })} />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={async () => { await createLineItem.mutateAsync({ name: newRow.name, unit_value: newRow.unit_value, quantity: newRow.quantity }); setNewRow(null); }} disabled={createLineItem.isPending || !newRow.name.trim() || !newRow.unit_value} className="text-xs px-3 py-1 bg-[var(--color-primary)] text-white rounded disabled:opacity-50">Dodaj</button>
                  <button onClick={() => setNewRow(null)} className="text-xs px-3 py-1 border border-gray-300 text-gray-500 rounded">Anuluj</button>
                </div>
              </div>
            )}
            {lineItems.length > 0 && (
              <div className="pt-2 flex justify-between items-center">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Razem</span>
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                  {formatAmount(lineItems.reduce((s, i) => s + parseFloat(i.total), 0).toFixed(2))} PLN
                </span>
              </div>
            )}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-[520px]">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                  <th className="text-left py-1.5 pr-3 w-8 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Lp</th>
                  <th className="text-left py-1.5 pr-3 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nazwa pozycji</th>
                  <th className="text-right py-1.5 pr-3 w-32 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Wartość jedn. (netto)</th>
                  <th className="text-right py-1.5 pr-3 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ilość</th>
                  <th className="text-right py-1.5 pr-3 w-32 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Iloczyn</th>
                  {canEdit && invoice.status === "draft" && <th className="w-16 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />}
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, idx) => (
                  <tr key={item.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    {editingItem?.id === item.id ? (
                      <>
                        <td className="py-1.5 pr-3 text-gray-400 text-xs">{idx + 1}</td>
                        <td className="py-1 pr-2">
                          <input autoFocus className="w-full border border-[var(--color-primary)] rounded px-2 py-1 text-xs bg-white dark:bg-gray-700" value={editingItem.name} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} />
                        </td>
                        <td className="py-1 pr-2">
                          <input type="number" step="0.01" className="w-full border border-[var(--color-primary)] rounded px-2 py-1 text-xs text-right bg-white dark:bg-gray-700" value={editingItem.unit_value} onChange={(e) => setEditingItem({ ...editingItem, unit_value: e.target.value })} />
                        </td>
                        <td className="py-1 pr-2">
                          <input type="number" step="0.001" min="0.001" className="w-full border border-[var(--color-primary)] rounded px-2 py-1 text-xs text-right bg-white dark:bg-gray-700" value={editingItem.quantity} onChange={(e) => setEditingItem({ ...editingItem, quantity: e.target.value })} />
                        </td>
                        <td className="py-1.5 pr-3 text-right text-xs text-gray-400">
                          {(parseFloat(editingItem.unit_value || "0") * parseFloat(editingItem.quantity || "0")).toFixed(2)}
                        </td>
                        <td className="py-1 pl-1">
                          <div className="flex gap-1">
                            <button onClick={async () => { await updateLineItem.mutateAsync({ itemId: item.id, data: { name: editingItem.name, unit_value: editingItem.unit_value, quantity: editingItem.quantity } }); setEditingItem(null); }} disabled={updateLineItem.isPending || !editingItem.name.trim()} className="text-[10px] px-1.5 py-0.5 bg-[var(--color-primary)] text-white rounded hover:bg-[var(--color-primary-hover)] disabled:opacity-50">Zap.</button>
                            <button onClick={() => setEditingItem(null)} className="text-[10px] px-1.5 py-0.5 border border-gray-300 text-gray-500 rounded hover:bg-gray-50">Anu.</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-1.5 pr-3 text-gray-400 text-xs">{idx + 1}</td>
                        <td className="py-1.5 pr-3 text-gray-800 dark:text-gray-200">{item.name}</td>
                        <td className="py-1.5 pr-3 text-right text-gray-700 dark:text-gray-300">{formatAmount(item.unit_value)}</td>
                        <td className="py-1.5 pr-3 text-right text-gray-700 dark:text-gray-300">{item.quantity}</td>
                        <td className="py-1.5 pr-3 text-right font-medium text-gray-900 dark:text-gray-100">{formatAmount(item.total)}</td>
                        {canEdit && invoice.status === "draft" && (
                          <td className="py-1 pl-1">
                            <div className="flex gap-1">
                              <button onClick={() => setEditingItem({ id: item.id, name: item.name, unit_value: item.unit_value, quantity: item.quantity })} className="text-[10px] px-1.5 py-0.5 border border-gray-300 text-gray-500 rounded hover:bg-gray-50">Ed.</button>
                              <button onClick={() => deleteLineItem.mutate(item.id)} disabled={deleteLineItem.isPending} className="text-[10px] px-1.5 py-0.5 border border-red-200 text-red-500 rounded hover:bg-red-50">Usuń</button>
                            </div>
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                ))}
                {newRow && (
                  <tr className="border-b border-[var(--color-primary)] dark:border-[var(--color-primary)] bg-[var(--color-primary-dim)]/40 dark:bg-[var(--color-primary-hover)]">
                    <td className="py-1.5 pr-3 text-gray-400 text-xs">{lineItems.length + 1}</td>
                    <td className="py-1 pr-2">
                      <input autoFocus placeholder="Nazwa pozycji" className="w-full border border-[var(--color-primary)] rounded px-2 py-1 text-xs bg-white dark:bg-gray-700" value={newRow.name} onChange={(e) => setNewRow({ ...newRow, name: e.target.value })} />
                    </td>
                    <td className="py-1 pr-2">
                      <input type="number" step="0.01" placeholder="0.00" className="w-full border border-[var(--color-primary)] rounded px-2 py-1 text-xs text-right bg-white dark:bg-gray-700" value={newRow.unit_value} onChange={(e) => setNewRow({ ...newRow, unit_value: e.target.value })} />
                    </td>
                    <td className="py-1 pr-2">
                      <input type="number" step="0.001" min="0.001" className="w-full border border-[var(--color-primary)] rounded px-2 py-1 text-xs text-right bg-white dark:bg-gray-700" value={newRow.quantity} onChange={(e) => setNewRow({ ...newRow, quantity: e.target.value })} />
                    </td>
                    <td className="py-1.5 pr-3 text-right text-xs text-gray-400">
                      {(parseFloat(newRow.unit_value || "0") * parseFloat(newRow.quantity || "0")).toFixed(2)}
                    </td>
                    <td className="py-1 pl-1">
                      <div className="flex gap-1">
                        <button onClick={async () => { await createLineItem.mutateAsync({ name: newRow.name, unit_value: newRow.unit_value, quantity: newRow.quantity }); setNewRow(null); }} disabled={createLineItem.isPending || !newRow.name.trim() || !newRow.unit_value} className="text-[10px] px-1.5 py-0.5 bg-[var(--color-primary)] text-white rounded hover:bg-[var(--color-primary-hover)] disabled:opacity-50">Dod.</button>
                        <button onClick={() => setNewRow(null)} className="text-[10px] px-1.5 py-0.5 border border-gray-300 text-gray-500 rounded hover:bg-gray-50">Anu.</button>
                      </div>
                    </td>
                  </tr>
                )}
                {lineItems.length > 0 && (
                  <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 font-semibold">
                    <td /><td className="py-2 pr-3 text-xs text-gray-500">RAZEM</td><td /><td />
                    <td className="py-2 pr-3 text-right text-sm text-gray-900 dark:text-gray-100">
                      {formatAmount(lineItems.reduce((s, i) => s + parseFloat(i.total), 0).toFixed(2))} PLN
                    </td>
                    {canEdit && invoice.status === "draft" && <td />}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {lineItems.length === 0 && !newRow && (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">Brak pozycji</p>
          )}

          {/* Mismatch warning */}
          {(() => {
            if (lineItems.length === 0) return null;
            const itemsTotal = lineItems.reduce((s, i) => s + parseFloat(i.total), 0);
            const invNet = parseFloat(invoice.amount_net);
            const invGross = parseFloat(invoice.amount_gross);
            if (Math.abs(itemsTotal - invNet) < 0.01) return null;
            return (
              <div className="mt-3 flex items-center justify-between gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2">
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  <strong>Niezgodność:</strong> suma pozycji{" "}
                  <strong>{formatAmount(itemsTotal.toFixed(2))} PLN netto</strong>{" "}
                  ≠ kwota netto FV{" "}
                  <strong>{formatAmount(invoice.amount_net)} PLN</strong>
                </p>
                <button
                  onClick={() => {
                    const newNet = itemsTotal.toFixed(2);
                    const newGross = invNet > 0
                      ? (itemsTotal * invGross / invNet).toFixed(2)
                      : itemsTotal.toFixed(2);
                    if (!confirm(
                      `Zaktualizować kwotę faktury?\n\nNowe kwoty:\n• Netto: ${formatAmount(newNet)} PLN\n• Brutto: ${formatAmount(newGross)} PLN\n\nPotwierdź, aby zapisać.`
                    )) return;
                    updateMutation.mutate({ id: invoice.id, data: { amount_net: newNet, amount_gross: newGross } });
                  }}
                  disabled={updateMutation.isPending}
                  className="flex-shrink-0 text-xs px-2.5 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 font-medium whitespace-nowrap"
                >
                  Zaktualizuj kwotę FV
                </button>
              </div>
            );
          })()}
        </div>

        {/* Details grid */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-gray-100 dark:border-gray-700 pt-4">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Spółka wystawiająca</p>
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{invoice.company_name ?? "—"}</p>
          </div>
          <div className="col-span-2 sm:col-span-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">Klient (nabywca)</p>
            {invoice.client_name ? (
              <div className="mt-0.5 space-y-0.5">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{invoice.client_name}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {invoice.client_nip_resolved && <span>NIP: {invoice.client_nip_resolved}</span>}
                  {invoice.client_krs && <span>KRS: {invoice.client_krs}</span>}
                  {invoice.client_regon && <span>REGON: {invoice.client_regon}</span>}
                </div>
                {invoice.client_address && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-line">{invoice.client_address}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">—</p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Data wystawienia</p>
            {showInvoiceDateForm ? (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <DatePicker
                  value={invoiceDateInput}
                  onChange={setInvoiceDateInput}
                />
                <button
                  onClick={async () => {
                    if (!invoiceDateInput || invoiceDateInput === invoice.invoice_date) {
                      setShowInvoiceDateForm(false);
                      return;
                    }
                    await updateMutation.mutateAsync({
                      id: invoice.id,
                      data: { invoice_date: invoiceDateInput },
                    });
                    setShowInvoiceDateForm(false);
                  }}
                  disabled={updateMutation.isPending || !invoiceDateInput}
                  className="text-xs px-2 py-1 bg-[var(--color-primary)] text-white rounded hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
                >
                  Zapisz
                </button>
                <button
                  onClick={() => setShowInvoiceDateForm(false)}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Anuluj
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{formatDate(invoice.invoice_date, "")}</p>
                {canEditFooter && invoice.status === "draft" && (
                  <button
                    onClick={() => {
                      setInvoiceDateInput(invoice.invoice_date || "");
                      setShowInvoiceDateForm(true);
                    }}
                    className="text-xs text-[var(--color-primary)] hover:underline"
                    title="Edycja możliwa tylko do momentu wystawienia faktury"
                  >
                    Zmień
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Termin płatności (obliczony)</p>
            {showTermsForm ? (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <select
                  value={invoice.payment_terms_days}
                  onChange={async (e) => {
                    await updateMutation.mutateAsync({
                      id: invoice.id,
                      data: { payment_terms_days: parseInt(e.target.value) },
                    });
                    setShowTermsForm(false);
                  }}
                  disabled={updateMutation.isPending}
                  className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                >
                  {PAYMENT_TERM_DAY_OPTIONS.map((d) => (
                    <option key={d} value={d}>
                      {d} dni
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setShowTermsForm(false)}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Anuluj
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {formatDate(invoice.payment_due_date)}
                  <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">({invoice.payment_terms_days}d)</span>
                </p>
                {canEdit && (
                  <button
                    onClick={() => setShowTermsForm(true)}
                    className="text-xs text-[var(--color-primary)] hover:underline"
                    title="Zmień kanoniczny termin płatności (dni)"
                  >
                    Zmień
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="col-span-2 sm:col-span-1">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
              Przewidywany termin płatności
              {invoice.expected_payment_date && (
                <span className="ml-1 text-[10px] font-semibold text-[var(--color-primary)] bg-[var(--color-primary-dim)] px-1 rounded">ręczny</span>
              )}
            </p>
            {showExpectedDateForm ? (
              <div className="flex items-center gap-2 mt-1">
                <DatePicker
                  value={expectedDateInput}
                  onChange={setExpectedDateInput}
                />
                <button
                  onClick={async () => {
                    await setExpectedDate.mutateAsync(expectedDateInput || null);
                    setShowExpectedDateForm(false);
                  }}
                  disabled={setExpectedDate.isPending}
                  className="text-xs px-2 py-1 bg-[var(--color-primary)] text-white rounded hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
                >
                  Zapisz
                </button>
                {invoice.expected_payment_date && (
                  <button
                    onClick={async () => {
                      await setExpectedDate.mutateAsync(null);
                      setShowExpectedDateForm(false);
                    }}
                    disabled={setExpectedDate.isPending}
                    className="text-xs px-2 py-1 border border-gray-300 text-gray-500 rounded hover:bg-gray-50"
                  >
                    Usuń
                  </button>
                )}
                <button
                  onClick={() => setShowExpectedDateForm(false)}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Anuluj
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {formatDate(invoice.expected_payment_date ?? invoice.payment_due_date)}
                </p>
                {canEdit && (
                  <button
                    onClick={() => {
                      setExpectedDateInput(invoice.expected_payment_date ?? invoice.payment_due_date ?? "");
                      setShowExpectedDateForm(true);
                    }}
                    className="text-xs text-[var(--color-primary)] hover:underline"
                  >
                    Zmień
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Kwota netto</p>
            {showNetEdit ? (
              <div className="mt-1 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    type="number"
                    step="0.01"
                    min="0"
                    value={netInput}
                    onChange={(e) => setNetInput(e.target.value)}
                    className="w-36 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                    placeholder="0.00"
                  />
                  <span className="text-xs text-gray-500">PLN</span>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 dark:text-gray-400">VAT:</label>
                  <select
                    value={vatRate}
                    onChange={(e) => setVatRate(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none"
                  >
                    {["0", "5", "8", "23"].map((r) => (
                      <option key={r} value={r}>{r}%</option>
                    ))}
                  </select>
                  <span className="text-xs text-gray-400">→ brutto: <strong className="text-gray-700 dark:text-gray-200">{formatAmount(String((parseFloat(netInput.replace(",", ".")) || 0) * (1 + parseInt(vatRate) / 100)))} PLN</strong></span>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const net = parseFloat(netInput.replace(",", "."));
                      if (!net || net <= 0) return;
                      const gross = net * (1 + parseInt(vatRate) / 100);
                      updateMutation.mutate({
                        id: invoice.id,
                        data: {
                          amount_net: net.toFixed(2),
                          amount_gross: gross.toFixed(2),
                        },
                      }, { onSuccess: () => setShowNetEdit(false) });
                    }}
                    disabled={updateMutation.isPending || !parseFloat(netInput.replace(",", ".") || "0")}
                    className="text-xs px-2.5 py-1 bg-[var(--color-primary)] text-white rounded hover:opacity-90 disabled:opacity-50"
                  >
                    Zapisz
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNetEdit(false)}
                    className="text-xs px-2.5 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Anuluj
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{formatAmount(invoice.amount_net)} PLN</p>
                {invoice.status === "draft" && canEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      setNetInput(invoice.amount_net);
                      const net = parseFloat(invoice.amount_net);
                      const gross = parseFloat(invoice.amount_gross);
                      if (net > 0) {
                        const ratio = gross / net - 1;
                        const closest = [0, 0.05, 0.08, 0.23].reduce((a, b) =>
                          Math.abs(b - ratio) < Math.abs(a - ratio) ? b : a
                        );
                        setVatRate(String(Math.round(closest * 100)));
                      }
                      setShowNetEdit(true);
                    }}
                    className="text-xs text-[var(--color-primary)] hover:underline"
                  >
                    Edytuj
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Kwota brutto</p>
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{formatAmount(invoice.amount_gross)} PLN</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Zatwierdzenie dyrektora</p>
            {invoice.director_approved === true ? (
              <p className="text-sm font-medium text-green-700 dark:text-green-400">Zatwierdzone</p>
            ) : invoice.director_approved === false ? (
              <p className="text-sm font-medium text-red-600 dark:text-red-400">Cofnięte</p>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">Oczekuje</p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Utworzył</p>
            <p className="text-sm text-gray-700 dark:text-gray-300">{invoice.created_by_name}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">{formatDateTime(invoice.created_at)}</p>
          </div>
          {invoice.paid_amount && invoice.paid_amount !== "0.00" && (() => {
            const gross = parseFloat(invoice.amount_gross);
            const net = parseFloat(invoice.amount_net);
            const paid = parseFloat(invoice.paid_amount);
            const netPaid = gross > 0 ? paid * (net / gross) : 0;
            const vatPaid = paid - netPaid;
            return (
              <div className="col-span-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">Zapłacono</p>
                <p className="text-sm font-medium text-green-700">{formatAmount(invoice.paid_amount)} PLN brutto</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  w tym VAT: {formatAmount(vatPaid.toFixed(2))} PLN
                  {netPaid > 0 && <>, netto: {formatAmount(netPaid.toFixed(2))} PLN</>}
                </p>
                {invoice.payment_date && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">Data wpływu: {formatDate(invoice.payment_date)}</p>
                )}
              </div>
            );
          })()}
          {invoice.payment_comment && (
            <div className="col-span-2">
              <p className="text-xs text-gray-500 dark:text-gray-400">Komentarz płatności</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{invoice.payment_comment}</p>
            </div>
          )}
          {invoice.description && (
            <div className="col-span-2 sm:col-span-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Opis</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{invoice.description}</p>
            </div>
          )}
          <div className="col-span-2 sm:col-span-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Stopka faktury</p>
              {canEditFooter && (
                <button
                  type="button"
                  onClick={() => { setFooterDraft(invoice.footer_text || ""); setShowFooterModal(true); }}
                  className="text-xs text-[var(--color-primary)] dark:text-indigo-400 hover:underline"
                >
                  {invoice.footer_text ? "Edytuj" : "Dodaj"}
                </button>
              )}
            </div>
            {invoice.footer_text ? (
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap mt-1">
                {invoice.footer_text}
              </p>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500 italic mt-1">brak</p>
            )}
          </div>
        </div>

        {/* Linked planned revenue */}
        {invoice.planned_revenue_id && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Powiązany planowany przychód</p>
            <div className="flex items-center gap-4 flex-wrap">
              <Link
                to="/finances/planned-revenues"
                className="text-sm text-[var(--color-primary)] dark:text-indigo-400 hover:underline font-medium"
              >
                {invoice.planned_revenue_description}
              </Link>
              {invoice.planned_revenue_amount && (
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {formatAmount(invoice.planned_revenue_amount)} PLN
                </span>
              )}
              {invoice.planned_revenue_expected_date && (
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  planowana data: {formatDate(invoice.planned_revenue_expected_date)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Faktury partnera JV (konsorcjum) */}
        <JointVentureSection invoice={invoice} />
      </div>

      {/* Event history */}
      <div ref={eventsRef} className="scroll-mt-4 bg-white dark:bg-gray-800 p-4 sm:p-6">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">Historia zdarzeń</h2>

        {eventsLoading ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Wczytywanie...</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak zdarzeń.</p>
        ) : (
          <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-2">
            {events.map((ev) => {
              const dotColor =
                ev.event_type === "created" ? "bg-blue-400" :
                ev.event_type === "status_changed" ? "bg-indigo-400" :
                ev.event_type === "payment_updated" ? "bg-[var(--bg-elevated)]0" :
                ev.event_type === "file_uploaded" ? "bg-purple-400" :
                ev.event_type === "comment" ? "bg-gray-300 dark:bg-gray-600" :
                ev.event_type === "sent_to_investor" ? "bg-teal-400" :
                ev.event_type === "awaiting_accountant" ? "bg-orange-400" :
                "bg-gray-300";
              return (
                <li key={ev.id} className="ml-5 pb-5 last:pb-0">
                  <div className={`absolute -left-1 mt-1.5 w-2 h-2 rounded-full ${dotColor}`} />
                  <div className="flex items-baseline justify-between gap-4 flex-wrap mb-0.5">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                      {CLIENT_INVOICE_EVENT_LABELS[ev.event_type]}
                    </span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {ev.user_name} · {formatDateTime(ev.created_at)}
                      </span>
                      {canEdit && ev.event_type === "payment_updated" && ev.amount && (
                        <button
                          onClick={() => {
                            if (confirm("Czy na pewno cofnąć ten wpływ? Kwota zostanie odjęta od sumy zapłaconej.")) {
                              deletePaymentEvent.mutate(ev.id);
                            }
                          }}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Cofnij
                        </button>
                      )}
                    </div>
                  </div>
                  {ev.event_type === "comment" ? (
                    isHtmlContent(ev.description) ? (
                      <div
                        className="rich-text-content text-xs text-gray-700 dark:text-gray-300"
                        dangerouslySetInnerHTML={{
                          __html: highlightMentionsInHtml(
                            sanitize(ev.description),
                            allUsers.map((u) => u.full_name),
                          ),
                        }}
                      />
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-line leading-relaxed">
                        {highlightMentions(ev.description, allUsers.map((u) => u.full_name))}
                      </p>
                    )
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-line leading-relaxed">
                      {ev.description}
                    </p>
                  )}
                  {ev.file_details.length > 0 && (
                    <div className="flex flex-col gap-0.5 mt-1">
                      {ev.file_details.map((f) => f.url && (
                        <a
                          key={f.id}
                          href={f.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-[var(--color-primary)] hover:underline"
                        >
                          {f.original_filename}
                        </a>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}

        {/* Add comment */}
        {canEdit && (
          <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-2">Dodaj uwagę / komentarz</label>
            <div className="flex flex-col gap-2">
              <RichTextEditor
                value={comment}
                onChange={(html) => setComment(html)}
                mentionSuggestions={mentionSuggestions}
                onMentionIdsChange={setCommentMentionIds}
                disabled={addComment.isPending}
                placeholder="Wpisz uwagę… (@ = oznaczenie użytkownika)"
              />
              <div className="flex flex-col gap-1.5">
                {commentFiles.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {commentFiles.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded px-2 py-0.5">
                        {f.name}
                        <button
                          type="button"
                          onClick={() => setCommentFiles((prev) => prev.filter((_, j) => j !== i))}
                          className="text-gray-400 hover:text-red-500 leading-none"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <input
                      ref={commentFileRef}
                      type="file"
                      multiple
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls"
                      onChange={(e) => {
                        const picked = Array.from(e.target.files ?? []);
                        setCommentFiles((prev) => [...prev, ...picked]);
                        if (commentFileRef.current) commentFileRef.current.value = "";
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => commentFileRef.current?.click()}
                      className="text-xs text-gray-500 dark:text-gray-400 hover:text-[var(--color-primary)] border border-gray-300 dark:border-gray-600 rounded px-2 py-1"
                    >
                      Załącz pliki
                    </button>
                  </div>
                  <Button
                    size="sm" loading={addComment.isPending}
                    onClick={handleAddComment} disabled={!comment}
                  >
                    Dodaj
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef} type="file" className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        onChange={handleFileChange}
      />

      {/* Issue modal */}
      {showIssueModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Wystaw fakturę PRZ
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Podaj numer i dołącz plik PDF faktury przed wystawieniem.
            </p>
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Nr faktury *</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="np. FV/2026/001"
                  value={issueNumber}
                  onChange={(e) => setIssueNumber(e.target.value)}
                />
                {!issueNumber.trim() && <p className="text-xs text-red-500 mt-1">Wymagany przed wystawieniem.</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Data wystawienia</label>
                <DatePicker
                  value={issueDate}
                  onChange={setIssueDate}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Plik faktury *</label>
                <FileDropZone
                  files={issueFile ? [issueFile] : []}
                  onFilesChange={(fs) => setIssueFile(fs[0] ?? null)}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                {!issueFile && <p className="text-xs text-red-500 mt-1">Wymagany przed wystawieniem.</p>}
              </div>
              <div className="flex gap-2 mt-1">
                <Button size="sm" loading={updateMutation.isPending} onClick={handleIssue} disabled={!issueFile || !issueNumber.trim()}>
                  Wystaw fakturę
                </Button>
                <Button size="sm" variant="secondary" onClick={() => { setShowIssueModal(false); setIssueFile(null); setIssueNumber(""); setIssueDate(""); }}>
                  Anuluj
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer text modal */}
      {showFooterModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-lg">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Stopka faktury
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Wieloliniowy tekst drukowany pod fakturą (np. dane do przelewu, podpisy, uwagi). Bez HTML — same łamania linii zostaną zachowane.
            </p>
            <textarea
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono"
              rows={8}
              value={footerDraft}
              onChange={(e) => setFooterDraft(e.target.value)}
              placeholder="np.&#10;Bank: PKO BP&#10;Konto: 12 3456 7890 1234 5678 9012 3456&#10;&#10;Dziękujemy za współpracę."
            />
            <div className="flex gap-2 mt-4">
              <Button
                size="sm"
                loading={updateMutation.isPending}
                onClick={() => {
                  if (!invoice) return;
                  updateMutation.mutate(
                    { id: invoice.id, data: { footer_text: footerDraft } },
                    { onSuccess: () => setShowFooterModal(false) },
                  );
                }}
              >
                Zapisz
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setShowFooterModal(false)}>
                Anuluj
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
